// ABOUTME: Renders a prototype compatibility matrix cell: centered status chip with a Full/Define checkbox inside it.
// ABOUTME: An edit pencil appears on the right only while the cell is hovered; reads pair state/handlers from CompatibilityMatrixCellContext; empty on the diagonal.

import {
    Box,
    Checkbox,
    EditOutlinedIcon,
    FormControlLabel,
    IconButton,
    lighten,
} from '@ds/coolshop';
import type { CustomCellRendererProps } from '@ds/data-table';
import type { CellContext } from '@tanstack/react-table';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import {
    CompatibilityCellState,
    useCompatibilityMatrixCell,
} from './compatibilityMatrixCellContext';

const COLUMN_ID_PREFIX = 'compatibility-';

const CHIP_COLOR: Record<Exclude<CompatibilityCellState, 'define'>, 'success' | 'warning'> = {
    full: 'success',
    partial: 'warning',
};

const CHECKBOX_COLOR: Record<CompatibilityCellState, 'primary' | 'success' | 'warning'> = {
    full: 'success',
    partial: 'warning',
    define: 'primary',
};

const DEFAULT_LABEL: Record<CompatibilityCellState, string> = {
    full: 'Completa',
    partial: 'Parziale',
    define: 'Non definita',
};

// Only `row` and `params` are read; the narrower prop type lets this render both as a
// registered custom cell renderer and as a `customColumns` cell callback.
type Props = Pick<CustomCellRendererProps, 'row' | 'params'>;

export default function GridCellCompatibilityRender(props: Props) {
    const { row, params } = props;
    const intl = useIntl();
    const ctx = useCompatibilityMatrixCell();
    const [hovered, setHovered] = useState(false);

    const cellContext = params as CellContext<any, any>;
    const columnId = cellContext.cell.column.id;

    if (!ctx || !columnId.startsWith(COLUMN_ID_PREFIX)) return null;

    const colCode = columnId.slice(COLUMN_ID_PREFIX.length);
    const colPrototypeId = ctx.colCodeToId[colCode];
    const rowPrototypeId = row.id;

    // A prototype is never compatible with itself: leave the diagonal empty.
    if (!colPrototypeId || colPrototypeId === rowPrototypeId) return null;

    const state = ctx.getCellState(rowPrototypeId, colPrototypeId);

    const label = intl.formatMessage({
        id: `prototype-compatibility-matrix.cell.${state}`,
        defaultMessage: DEFAULT_LABEL[state],
    });

    return (
        <Box
            position="relative"
            display="flex"
            justifyContent="center"
            alignItems="center"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Box
                bgcolor={t =>
                    state === 'define'
                        ? t.palette.action.selected
                        : lighten(t.palette[CHIP_COLOR[state]].main, 0.8)
                }
                borderRadius={16}
                height={24}
                width={118}
                display="flex"
                alignItems="center"
                px={1}
            >
                <FormControlLabel
                    label={label}
                    control={
                        <Checkbox
                            size="small"
                            checked={state === 'full'}
                            disabled={ctx.readOnly}
                            indeterminate={state === 'partial'}
                            color={CHECKBOX_COLOR[state]}
                            onChange={() => ctx.onToggle(rowPrototypeId, colPrototypeId)}
                        />
                    }
                />
            </Box>
            <Box
                position="absolute"
                right={0}
                top={0}
                bottom={0}
                display="flex"
                alignItems="center"
            >
                <IconButton
                    size="small"
                    visible={hovered}
                    disabled={ctx.readOnly}
                    onClick={() => ctx.onEditPencil(rowPrototypeId, colPrototypeId)}
                >
                    <EditOutlinedIcon fontSize="small" color="secondary" />
                </IconButton>
            </Box>
        </Box>
    );
}
